import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BackButton,
  EmptyState,
  Footer,
  Icon,
  Spinner,
  TextButton,
  Typography,
  TypographyType,
} from '../../components';
import { AppRoutes } from '../../constants';
import { useGetPromptTemplatesQuery } from '../../services';
import { PromptTemplatesSelectMode, PromptTemplateStatus } from '../../types';
import ManagePromptTemplatePage from '../ManagePromptTemplatePage';
import PromptTemplateRow from './PromptTemplateRow';

const PromptTemplatesPage = () => {
  const navigate = useNavigate();

  const [currPromptTemplateId, setCurrPromptTemplateId] = useState<string>();

  const listRef = useRef<HTMLDivElement>(null);

  const { data: promptTemplates = [], isLoading } = useGetPromptTemplatesQuery({
    selectMode: PromptTemplatesSelectMode.ALL,
  });

  // Sort the prompt templates by archived status and updatedAt date.
  const sortedPromptTemplates = useMemo(
    () =>
      [...promptTemplates].sort((a, b) => {
        // First sort by archived status.
        if (a.status === PromptTemplateStatus.ARCHIVED && b.status !== PromptTemplateStatus.ARCHIVED) return 1;
        if (a.status !== PromptTemplateStatus.ARCHIVED && b.status === PromptTemplateStatus.ARCHIVED) return -1;
        // Then sort by updatedAt date.
        return b.updatedAt.localeCompare(a.updatedAt);
      }),
    [promptTemplates]
  );

  // Scrolls to the top of the list.
  const scrollToTop = useCallback(() => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // When a prompt template is selected from the list:
  // 1. We find the template in our existing list data (no need to fetch)
  // 2. Pass it directly to ManagePromptTemplatePage via the promptTemplate prop
  // 3. This avoids an unnecessary API call since we already have the data
  if (currPromptTemplateId) {
    const currPromptTemplate = promptTemplates.find((template) => template.id === currPromptTemplateId);
    return (
      <ManagePromptTemplatePage promptTemplate={currPromptTemplate} onBack={() => setCurrPromptTemplateId(undefined)} />
    );
  }

  // flex-col is needed to make the header sticky
  return (
    <div className="bg-base-100 flex h-full flex-col">
      <div className="flex items-center gap-4 px-8 pt-4">
        <BackButton onBack={() => navigate(AppRoutes.SIMULATIONS)} />
        <Typography type={TypographyType.H3}>Prompt templates</Typography>
      </div>
      {!sortedPromptTemplates.length && <EmptyState description="No prompt templates" icon={Icon.SQUARE_TERMINAL} />}
      {sortedPromptTemplates.length && (
        <div ref={listRef} className="flex flex-grow justify-center overflow-y-auto py-8">
          <div className="flex h-fit w-full max-w-[700px] flex-col gap-4">
            {sortedPromptTemplates.map((promptTemplate) => (
              <PromptTemplateRow
                key={promptTemplate.id}
                promptTemplate={promptTemplate}
                openPromptTemplate={() => setCurrPromptTemplateId(promptTemplate.id)}
                scrollToTop={scrollToTop}
              />
            ))}
          </div>
        </div>
      )}
      <Footer className="justify-end">
        <TextButton text="Create new" onClick={() => navigate(AppRoutes.PROMPT_TEMPLATES_CREATE)} />
      </Footer>
    </div>
  );
};

export default PromptTemplatesPage;
